import { FC, useState, useEffect, ReactNode } from 'react';

import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import { Link } from 'dibs-elements/exports/Link';

import classnames from 'classnames';

import styles from './main.scss';

export type Step = {
    label: ReactNode;
    onClick?: () => void;
    dataTn?: string;
};

export type Props = {
    steps: Step[];
    currentStep: number;
    disableNavigation?: boolean;
    dataTn?: string;
};

export const StepNavigator: FC<Props> = ({
    steps,
    currentStep,
    disableNavigation = false,
    dataTn,
}) => {
    const [furthestNavigableStep, setFurthestNavigableStep] = useState(currentStep);

    const isClickableNav = (index: number): boolean => {
        return index !== currentStep && index <= furthestNavigableStep && !disableNavigation;
    };

    const handleNavClick = (isClickable: boolean, onClick?: () => void): void => {
        if (isClickable && onClick) {
            onClick();
        }
    };

    useEffect(() => {
        if (currentStep) {
            if (currentStep > furthestNavigableStep) {
                setFurthestNavigableStep(currentStep);
            }
        }
    }, [currentStep, furthestNavigableStep]);

    return steps?.length < 1 ? null : (
        <ol className={styles.steps} data-tn={dataTn}>
            {steps?.map((step, index) => {
                const { label, onClick, dataTn: stepDataTn } = step;
                const isClickable = isClickableNav(index);
                return (
                    <div key={index} className={styles.flex}>
                        <Link
                            className={classnames(styles.step, {
                                [styles.clickable]: isClickable,
                                [styles.unavailableStep]: !isClickable && !disableNavigation,
                                [styles.currentStep]: index === currentStep,
                            })}
                            onClick={() => {
                                handleNavClick(isClickable, onClick);
                            }}
                            onKeyDown={() => handleNavClick(isClickable, onClick)}
                            tabIndex={0}
                            underline="none"
                            dataTn={stepDataTn}
                        >
                            {label}
                        </Link>
                        {index !== steps.length - 1 ? (
                            <ArrowRight className={styles.arrow} />
                        ) : null}
                    </div>
                );
            })}
        </ol>
    );
};
